import { useCallback, useState } from 'react'
import type { ReactElement } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import { formatHumanName } from '@medplum/core'
import { notifications } from '@mantine/notifications'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { SaveDraftCoordinatorModal, type OrderTypeLabel } from '../SaveDraftCoordinatorModal'
import type { CareCoordinator } from './useCareCoordinators'
import { getErrorMessage, logError } from './errorUtils'

type Options = {
  patient: Patient
  orderType: OrderTypeLabel
  skipFacility: boolean
  coordinatorNote: string
  alreadySaved: boolean
  submitting: boolean
  onSaveDraft: () => Promise<boolean>
}

type Result = {
  modal: ReactElement
  triggerSaveDraft: () => Promise<void>
}

function getPatientName(patient: Patient): string {
  const first = patient.name?.[0]
  if (!first) return ''
  return formatHumanName(first) || ''
}

export function useSaveDraftCoordinatorFlow({
  patient,
  orderType,
  skipFacility,
  coordinatorNote,
  alreadySaved,
  submitting,
  onSaveDraft,
}: Options): Result {
  const middleware = useMiddlewareClient()
  const [opened, setOpened] = useState(false)
  const [sending, setSending] = useState(false)

  const shouldPromptCoordinator = skipFacility && !alreadySaved
  const saving = submitting || sending

  const triggerSaveDraft = useCallback(async () => {
    if (shouldPromptCoordinator) {
      setOpened(true)
      return
    }
    await onSaveDraft()
  }, [shouldPromptCoordinator, onSaveDraft])

  const close = useCallback(() => {
    if (!saving) setOpened(false)
  }, [saving])

  const handleSaveDraftOnly = useCallback(async () => {
    setSending(true)
    try {
      const saved = await onSaveDraft()
      if (saved) {
        setOpened(false)
      }
    } finally {
      setSending(false)
    }
  }, [onSaveDraft])

  const handleSaveAndSend = useCallback(
    async (input: { coordinator: CareCoordinator; subject: string; message: string }) => {
      setSending(true)
      try {
        const saved = await onSaveDraft()
        if (!saved) {
          return
        }

        try {
          await middleware.post('/threads', {
            // Inherently one coordinator, but the endpoint now takes a list.
            recipientPractitionerIds: [input.coordinator.id],
            subjectPatientId: patient.id,
            subject: input.subject,
            initialMessage: input.message,
          })
          notifications.show({
            title: 'Message sent',
            message: `Message sent to ${input.coordinator.name}.`,
            color: 'green',
          })
        } catch (err) {
          logError('Send coordinator message', err)
          notifications.show({
            title: 'Message not sent',
            message: `Draft saved, but the message to ${input.coordinator.name} failed: ${getErrorMessage(
              err,
            )}. Please send it from the Messages page.`,
            color: 'red',
            autoClose: 10_000,
          })
        }
        setOpened(false)
      } finally {
        setSending(false)
      }
    },
    [middleware, onSaveDraft, patient.id],
  )

  const modal = (
    <SaveDraftCoordinatorModal
      opened={opened}
      patientName={getPatientName(patient)}
      orderType={orderType}
      defaultNote={coordinatorNote}
      saving={saving}
      onCancel={close}
      onSaveDraftOnly={handleSaveDraftOnly}
      onSaveAndSend={handleSaveAndSend}
    />
  )

  return { modal, triggerSaveDraft }
}
